import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { scrollToConfigure } from "@/lib/useScrollReveal";
import { Truck, ShieldCheck, ArrowCounterClockwise, Snowflake, ArrowRight } from "@phosphor-icons/react";
import HeroModel from "./HeroModel";

const HERO_IMG =
  "https://images.unsplash.com/photo-1586078875290-c22eb791ad5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMHNtYXJ0JTIwc3BlYWtlciUyMGRldmljZXxlbnwwfHx8fDE3ODM4NDQwNjJ8MA&ixlib=rb-4.1.0&q=85";

function LiveTempCard() {
  const [temp, setTemp] = useState(4.2);
  useEffect(() => {
    const id = setInterval(() => {
      const next = 3.9 + Math.random() * 0.6;
      setTemp(Math.round(next * 10) / 10);
    }, 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      data-testid="live-temp-widget"
      className="rounded-2xl bg-white/8 backdrop-blur-xl border border-white/12 px-4 py-3 min-w-[190px]"
    >
      <div className="overline text-cyan-brand text-[9.5px] mb-1">Live Temp</div>
      <div className="flex items-baseline gap-2">
        <span className="font-display font-bold text-white text-[26px] tabular-nums leading-none">
          {temp.toFixed(1)}°C
        </span>
        <span className="font-mono-data text-[10px] text-white/60">±0.5</span>
        <span className="font-mono-data text-[10px] text-teal-brand uppercase tracking-widest ml-auto">
          Clinical
        </span>
      </div>
    </div>
  );
}

function BatteryCard() {
  return (
    <div className="rounded-2xl bg-white/8 backdrop-blur-xl border border-white/12 px-4 py-3 min-w-[170px]">
      <div className="overline text-cyan-brand text-[9.5px] mb-1">Battery</div>
      <div className="flex items-center gap-2">
        <span className="font-display font-bold text-white text-[26px] leading-none">
          10h
        </span>
        <span className="font-mono-data text-[10px] text-white/70 uppercase tracking-widest">
          Unplugged
        </span>
      </div>
      <div className="mt-2 h-1 w-full rounded-full bg-white/12 overflow-hidden">
        <div className="h-full w-[82%] bg-teal-brand rounded-full" />
      </div>
    </div>
  );
}

const TRUST = [
  { icon: Truck, title: "Worldwide", tag: "Shipping" },
  { icon: ShieldCheck, title: "1-Year", tag: "Warranty" },
  { icon: ArrowCounterClockwise, title: "14-Day", tag: "Returns" },
];

export default function Hero() {
  return (
    <section
      data-testid="hero"
      className="relative overflow-hidden bg-cool-white pt-12 sm:pt-16 pb-20 sm:pb-24"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left: Copy */}
          <div className="lg:col-span-6 xl:col-span-6 reveal">
            <div className="overline text-cyan-brand mb-6 inline-flex items-center gap-2">
              <Snowflake size={14} weight="bold" />
              CryoVault · Portable Peptide Fridge
            </div>
            <h1 className="font-display text-navy-deep font-bold text-[46px] sm:text-6xl lg:text-[76px] leading-[0.98] tracking-tight">
              Protect what you put
              <br />
              <span className="text-signature-gradient">inside your body.</span>
            </h1>
            <p className="mt-7 text-[17px] leading-relaxed text-slate-600 max-w-xl">
              Portable cooling engineered specifically for peptides, insulin and
              temperature-sensitive medications — locked at a clinical{" "}
              <span className="text-navy-deep font-semibold">2–8°C</span>,
              wherever life takes you.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative group/cta">
                {/* Hover ribbon */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-3 left-1/2 -translate-x-1/2 -translate-y-full opacity-0 group-hover/cta:opacity-100 group-hover/cta:-translate-y-[110%] transition-all duration-300 ease-out"
                >
                  <span className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-navy-deep text-white text-[10px] font-mono-data uppercase tracking-[0.22em] shadow-[0_10px_25px_-6px_rgba(2,47,107,0.5)] whitespace-nowrap">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal-brand pulse-dot" />
                    Now
                    <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 h-2 w-2 rotate-45 bg-navy-deep" />
                  </span>
                </span>

                {/* Animated glow aura */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-full bg-cta-gradient blur-xl opacity-20 group-hover/cta:opacity-45 scale-90 group-hover/cta:scale-[1.04] transition-all duration-500"
                />

                <Button
                  data-testid="hero-cta"
                  onClick={scrollToConfigure}
                  className="group relative h-14 px-8 rounded-full bg-cta-gradient text-white text-[15px] font-semibold active:scale-[0.98] transition-all duration-300 overflow-hidden border border-white/25 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.55),inset_0_-1px_0_rgba(2,47,107,0.35),0_10px_30px_-10px_rgba(2,47,107,0.35),0_2px_8px_-2px_rgba(1,207,198,0.45)] hover:brightness-110 hover:-translate-y-0.5 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.7),inset_0_-1px_0_rgba(2,47,107,0.35),0_16px_40px_-14px_rgba(2,47,107,0.45),0_0_30px_-6px_rgba(1,207,198,0.55)]"
                >
                  {/* liquid highlight sheen */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-3 top-0 h-1/2 rounded-full bg-gradient-to-b from-white/45 to-transparent opacity-90"
                  />
                  {/* Moving specular highlight on hover */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-y-1 -left-1/3 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/55 to-transparent opacity-0 group-hover:opacity-100 group-hover:left-[110%] transition-all duration-700 ease-out"
                  />
                  <span className="relative z-10 inline-flex items-center">
                    Protect Your Peptides{" "}
                    <ArrowRight
                      size={16}
                      weight="bold"
                      className="ml-1 transition-transform group-hover:translate-x-1"
                    />
                  </span>
                </Button>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#8A97A8]/25 px-4 h-11 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-teal-brand pulse-dot" />
                <span className="font-mono-data text-[11px] text-navy-deep uppercase tracking-widest">
                  4.2°C · Stable
                </span>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-5 max-w-lg">
              {TRUST.map((t) => (
                <div
                  key={t.title}
                  className="flex items-start gap-2.5"
                >
                  <div className="h-9 w-9 rounded-full bg-white border border-[#8A97A8]/25 grid place-items-center text-royal shrink-0">
                    <t.icon size={16} weight="regular" />
                  </div>
                  <div className="leading-tight">
                    <div className="text-navy-deep font-semibold text-sm">
                      {t.title}
                    </div>
                    <div className="font-mono-data text-[9.5px] text-cool-grey uppercase tracking-[0.22em] mt-0.5">
                      {t.tag}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Product stage */}
          <div className="lg:col-span-6 xl:col-span-6 reveal">
            <div className="relative rounded-[28px] p-6 sm:p-10 bg-hero-gradient overflow-hidden aspect-[5/4] shadow-[0_50px_120px_-30px_rgba(2,47,107,0.55)]">
              {/* Floating cards */}
              <div className="absolute left-6 top-6 z-10">
                <LiveTempCard />
              </div>
              <div className="absolute right-6 top-6 z-10">
                <BatteryCard />
              </div>

              {/* Radial glow behind */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[80%] w-[80%] rounded-full bg-[radial-gradient(circle_at_center,rgba(1,207,198,0.28),transparent_60%)]" />
              </div>

              {/* Product 3D model */}
              <div className="absolute inset-0">
                <HeroModel />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
